import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './bmi-chart.css'; // We'll create this file

type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active' | 'veryActive';
type WeightUnit = 'kg' | 'lb';
type Gender = 'male' | 'female';

// Define projection data type
interface ProjectionEntry {
  week: number;
  current: number;
  [key: `deficit${number}`]: number;
}

// Local storage key
const STORAGE_KEY = 'calorie-calculator-settings';

const CalorieDeficitCalculator = () => {
  // Load saved settings from localStorage
  const loadSavedSettings = () => {
    const savedSettings = localStorage.getItem(STORAGE_KEY);
    if (savedSettings) {
      try {
        return JSON.parse(savedSettings);
      } catch (error) {
        console.error('Failed to parse saved settings:', error);
      }
    }
    return null;
  };

  const savedSettings = loadSavedSettings();

  // State variables with localStorage persistence
  const [currentWeight, setCurrentWeight] = useState<number | string>(
    savedSettings?.currentWeight ?? 80
  );
  const [weightUnit, setWeightUnit] = useState<WeightUnit>(
    savedSettings?.weightUnit ?? 'kg'
  );
  const [timeFrame, setTimeFrame] = useState(
    savedSettings?.timeFrame ?? 12
  ); // Weeks
  const [deficitLevels, setDeficitLevels] = useState(
    savedSettings?.deficitLevels ?? [250, 500, 750, 1000]
  ); // Calories per day
  const [projectionData, setProjectionData] = useState<ProjectionEntry[]>([]);
  const [height, setHeight] = useState(
    savedSettings?.height ?? 175
  ); // cm
  const [age, setAge] = useState(
    savedSettings?.age ?? 30
  );
  const [gender, setGender] = useState<Gender>(
    savedSettings?.gender ?? 'male'
  );
  const [activityLevel, setActivityLevel] = useState<ActivityLevel>(
    savedSettings?.activityLevel ?? 'moderate'
  );
  const [saveNotification, setSaveNotification] = useState(false);
  // For forcing projection recalculation
  const [projectionTrigger, setProjectionTrigger] = useState(0);

  // Constants for calculations
  const CALORIES_PER_KG = 7700; // Approximately 7700 calories per kg of body weight
  const CALORIES_PER_POUND = 3500; // Approximately 3500 calories per pound of body weight
  
  // Activity level multipliers
  const ACTIVITY_MULTIPLIERS: Record<ActivityLevel, number> = {
    sedentary: 1.2, // Little or no exercise
    light: 1.375, // Light exercise 1-3 days/week
    moderate: 1.55, // Moderate exercise 3-5 days/week
    active: 1.725, // Hard exercise 6-7 days/week
    veryActive: 1.9 // Very hard exercise & physical job or training twice a day
  };

  // Calculate BMR using Mifflin-St Jeor Equation
  const calculateBMR = () => {
    const weightInKg = getWeightInKg();
    if (isNaN(Number(weightInKg))) return 0;
    
    if (gender === 'male') {
      return (10 * Number(weightInKg)) + (6.25 * height) - (5 * age) + 5;
    } else {
      return (10 * Number(weightInKg)) + (6.25 * height) - (5 * age) - 161;
    }
  };

  // Calculate TDEE (Total Daily Energy Expenditure)
  const calculateTDEE = () => {
    const bmr = calculateBMR();
    return Math.round(bmr * ACTIVITY_MULTIPLIERS[activityLevel]);
  };

  // Calculate weight projections when inputs change or projection trigger changes
  useEffect(() => {
    console.log("Main useEffect called with trigger:", projectionTrigger, "activity level:", activityLevel);
    calculateProjections();
  }, [currentWeight, weightUnit, timeFrame, deficitLevels, height, age, gender, activityLevel, projectionTrigger]);
  
  // Save settings to localStorage whenever they change
  useEffect(() => {
    const settings = {
      currentWeight,
      weightUnit,
      timeFrame,
      deficitLevels,
      height,
      age,
      gender,
      activityLevel
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    
    // Show save notification briefly
    setSaveNotification(true);
    const timer = setTimeout(() => {
      setSaveNotification(false);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [currentWeight, weightUnit, timeFrame, deficitLevels, height, age, gender, activityLevel]);

  // Convert weight if needed
  const getWeightInKg = (): number => {
    return weightUnit === 'kg' ? Number(currentWeight) : Number(currentWeight) * 0.453592;
  };

  // Convert weight for display
  const formatWeight = (weightInKg: number) => {
    if (isNaN(weightInKg)) return "0.0";
    return weightUnit === 'kg' 
      ? weightInKg.toFixed(1) 
      : (weightInKg / 0.453592).toFixed(1);
  };

  // Calculate projections based on inputs - using activityLevelArg to ensure current value
  const calculateProjections = () => {
    console.log("Calculate projections called, activity level:", activityLevel);
    
    // Force us to use the current activity level, not potentially stale values
    const currentActivityLevel = activityLevel;
    const activityMultiplier = ACTIVITY_MULTIPLIERS[currentActivityLevel];
    
    console.log(`Using activity level: ${currentActivityLevel} with multiplier: ${activityMultiplier}`);
    
    const data: ProjectionEntry[] = [];
    const baseWeightKg = getWeightInKg();
    
    // Check for valid weight value
    if (isNaN(baseWeightKg) || baseWeightKg <= 0) {
      setProjectionData([]);
      return;
    }
    
    // Calculate BMR directly here
    let bmr = 0;
    if (gender === 'male') {
      bmr = (10 * Number(baseWeightKg)) + (6.25 * height) - (5 * age) + 5;
    } else {
      bmr = (10 * Number(baseWeightKg)) + (6.25 * height) - (5 * age) - 161;
    }
    
    // Calculate TDEE directly with current activity level
    const currentTDEE = Math.round(bmr * activityMultiplier);
    for (let week = 0; week <= timeFrame; week++) {
      const entry: ProjectionEntry = { week, current: 0 };
      
      // Current weight with no deficit
      entry.current = baseWeightKg;
      
      // Calculate weight for each deficit level
      deficitLevels.forEach((deficit: number) => {
        // Check if deficit is too large (> 40% of TDEE)
        const safeDeficit = Math.min(deficit, currentTDEE * 0.4); 
        
        // Weekly calorie deficit
        const weeklyDeficit = safeDeficit * 7;
        // Weight loss in kg per week
        const weeklyLossKg = weeklyDeficit / CALORIES_PER_KG;
        // Projected weight after this many weeks
        const projectedWeightKg = Math.max(baseWeightKg - (weeklyLossKg * week), getMinHealthyWeight());
        
        // Store in data object
        entry[`deficit${deficit}`] = projectedWeightKg;
      });
      
      data.push(entry);
    }
    
    console.log("Setting projection data with", data.length, "entries, first week data:", data[0]);
    setProjectionData(data);
  };
  
  // Calculate minimum healthy weight (BMI of 18.5)
  const getMinHealthyWeight = () => {
    const heightInMeters = height / 100;
    return 18.5 * heightInMeters * heightInMeters;
  };
  
  // Calculate weight for a specific BMI value
  const getWeightForBMI = (bmi: number) => {
    const heightInMeters = height / 100;
    return bmi * heightInMeters * heightInMeters;
  };

  // Generate colors for chart lines
  const getLineColor = (index: number) => {
    const colors = ['#8884d8', '#4CAF50', '#FFC107', '#F44336', '#2196F3'];
    return colors[index % colors.length];
  };

  // Handle weight input change
  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value > 0) {
      setCurrentWeight(value);
    } else if (e.target.value === "") {
      setCurrentWeight("");
    }
  };

  // Handle numeric input change for any field
  type NumericSetter = React.Dispatch<React.SetStateAction<number>> | 
                       React.Dispatch<React.SetStateAction<number | string>>;
                       
  const handleNumericChange = <T extends NumericSetter>(setter: T) => 
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === "" ? "" : parseFloat(e.target.value);
      if (value === "" || (!isNaN(value) && value > 0)) {
        // @ts-ignore - this is safe because we're checking the value type
        setter(value);
      }
    };

  // Handle weight unit change
  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newUnit = e.target.value as WeightUnit;
    // Convert the weight when changing units
    if (newUnit !== weightUnit) {
      if (newUnit === 'kg' && currentWeight) {
        setCurrentWeight(parseFloat((Number(currentWeight) * 0.453592).toFixed(1)));
      } else if (currentWeight) {
        setCurrentWeight(parseFloat((Number(currentWeight) / 0.453592).toFixed(1)));
      }
      setWeightUnit(newUnit);
    }
  };

  // Format number for display
  const formatNumber = (num: number) => {
    return isNaN(num) ? "0" : num.toLocaleString();
  };
  
  // Reset all settings to defaults
  const resetSettings = () => {
    setCurrentWeight(80);
    setWeightUnit('kg');
    setTimeFrame(12);
    setDeficitLevels([250, 500, 750, 1000]);
    setHeight(175);
    setAge(30);
    setGender('male');
    setActivityLevel('moderate');
    localStorage.removeItem(STORAGE_KEY);
  };

  // Calculate BMI
  const calculateBMI = () => {
    const weightKg = getWeightInKg();
    const heightM = height / 100;
    const bmi = weightKg / (heightM * heightM);
    return isNaN(bmi) ? 0 : bmi;
  };

  // Get BMI category - aligned with BMI_RANGES
  const getBMICategory = (bmi: number) => {
    if (bmi < 18.5) return { text: "Underweight", color: "text-blue-500", bgColor: "bg-blue-100" };
    if (bmi < 25) return { text: "Normal weight", color: "text-green-500", bgColor: "bg-green-100" };
    if (bmi < 30) return { text: "Overweight", color: "text-yellow-500", bgColor: "bg-yellow-100" };
    if (bmi < 35) return { text: "Obesity (Class 1)", color: "text-orange-500", bgColor: "bg-orange-100" };
    if (bmi < 40) return { text: "Obesity (Class 2)", color: "text-red-500", bgColor: "bg-red-100" };
    return { text: "Severe Obesity (Class 3)", color: "text-red-700", bgColor: "bg-red-200" };
  };
  
  // BMI classification ranges - aligned with standard medical classifications
  const BMI_RANGES = [
    { min: 0, max: 18.5, label: "Underweight", color: "bg-blue-100" },
    { min: 18.5, max: 25, label: "Normal weight", color: "bg-green-100" },
    { min: 25, max: 30, label: "Overweight", color: "bg-yellow-100" },
    { min: 30, max: 35, label: "Obesity (Class 1)", color: "bg-orange-100" },
    { min: 35, max: 40, label: "Obesity (Class 2)", color: "bg-red-100" },
    { min: 40, max: 100, label: "Severe Obesity (Class 3)", color: "bg-red-200" }
  ];

  // Calculate macronutrient recommendations
  const calculateMacros = (calories: number) => {
    // Default macronutrient ratio (40% carbs, 30% protein, 30% fat)
    const carbs = Math.round((calories * 0.4) / 4); // 4 calories per gram of carbs
    const protein = Math.round((calories * 0.3) / 4); // 4 calories per gram of protein
    const fat = Math.round((calories * 0.3) / 9); // 9 calories per gram of fat
    return { carbs, protein, fat };
  };

  // Calculate BMR and TDEE directly - this ensures we have current values
  const currentBMR = (() => {
    const weightInKg = getWeightInKg();
    if (isNaN(Number(weightInKg))) return 0;
    
    if (gender === 'male') {
      return (10 * Number(weightInKg)) + (6.25 * height) - (5 * age) + 5;
    } else {
      return (10 * Number(weightInKg)) + (6.25 * height) - (5 * age) - 161;
    }
  })();
  
  const currentTDEE = Math.round(currentBMR * ACTIVITY_MULTIPLIERS[activityLevel]);
  
  // Current BMI
  const currentBMI = calculateBMI();
  const bmiCategory = getBMICategory(currentBMI);

  // Calculate macros
  const macros = calculateMacros(currentTDEE);

  console.log("Rendering with activity level:", activityLevel, "and projectionData length:", projectionData.length);
  
  return (
    <div className="p-4 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-center">Calorie Deficit & Weight Projection Calculator</h1>
            {saveNotification && (
              <span className="ml-3 text-sm text-green-600 bg-green-100 px-2 py-1 rounded animate-pulse">
                Settings saved!
              </span>
            )}
          </div>
          <button 
            onClick={resetSettings}
            className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded transition-colors"
            title="Reset to default values"
          >
            Reset
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Personal Info Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="font-semibold mb-3">Personal Information</h2>
            
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Current Weight</label>
                <div className="flex">
                  <input
                    type="number"
                    value={currentWeight}
                    onChange={handleWeightChange}
                    className="flex-grow p-2 border rounded-l"
                    min="30"
                    step="0.1"
                    placeholder="Enter weight"
                  />
                  <select
                    value={weightUnit}
                    onChange={handleUnitChange}
                    className="p-2 border border-l-0 rounded-r bg-gray-50"
                  >
                    <option value="kg">kg</option>
                    <option value="lb">lb</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Height (cm)</label>
                <input
                  type="number"
                  value={height}
                  onChange={handleNumericChange(setHeight)}
                  className="w-full p-2 border rounded"
                  min="100"
                  max="250"
                  step="1"
                  placeholder="Height in cm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Age</label>
                <input
                  type="number"
                  value={age}
                  onChange={handleNumericChange(setAge)}
                  className="w-full p-2 border rounded"
                  min="18"
                  max="100"
                  step="1"
                  placeholder="Age in years"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Gender</label>
                <select
                  value={gender}
                  onChange={(e) => setGender(e.target.value as Gender)}
                  className="w-full p-2 border rounded bg-white"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">Activity Level</label>
              <select
                value={activityLevel}
                onChange={(e) => {
                  const newActivityLevel = e.target.value as ActivityLevel;
                  console.log("Activity level changed from", activityLevel, "to", newActivityLevel);
                  setActivityLevel(newActivityLevel);
                  // Increment the projection trigger to force recalculation
                  setProjectionTrigger(prev => prev + 1);
                }}
                className="w-full p-2 border rounded bg-white"
              >
                <option value="sedentary">Sedentary (little or no exercise)</option>
                <option value="light">Lightly active (light exercise 1-3 days/week)</option>
                <option value="moderate">Moderately active (moderate exercise 3-5 days/week)</option>
                <option value="active">Very active (hard exercise 6-7 days/week)</option>
                <option value="veryActive">Extremely active (very hard exercise, physical job or training twice a day)</option>
              </select>
            </div>
          </div>
          
          {/* Current Status & Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="font-semibold mb-3">Current Status</h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 bg-white p-3 rounded border">
                <div className="text-lg">
                  BMI: <strong className={`${bmiCategory.color} ${bmiCategory.bgColor} px-2 py-0.5 rounded`}>
                    {currentBMI.toFixed(1)} - {bmiCategory.text}
                  </strong>
                </div>
                <div className="mt-2">
                  <div className="relative h-6 rounded-md overflow-hidden">
                    {/* BMI scale background with correct color positions */}
                    <div className="absolute inset-0" style={{
                      background: `linear-gradient(to right, 
                        var(--blue-100) 0%, 
                        var(--blue-100) ${18.5/40*100}%, 
                        var(--green-100) ${18.5/40*100}%, 
                        var(--green-100) ${25/40*100}%, 
                        var(--yellow-100) ${25/40*100}%, 
                        var(--yellow-100) ${30/40*100}%, 
                        var(--orange-100) ${30/40*100}%, 
                        var(--orange-100) ${35/40*100}%, 
                        var(--red-100) ${35/40*100}%, 
                        var(--red-100) ${40/40*100}%, 
                        var(--red-200) ${40/40*100}%, 
                        var(--red-200) 100%)`
                    }}></div>
                    
                    {/* Current BMI indicator */}
                    {currentBMI > 0 && (
                      <div className="absolute top-0 bottom-0" style={{ 
                        left: `${Math.min(currentBMI / 40 * 100, 100)}%`,
                        transform: 'translateX(-50%)'
                      }}>
                        <div className="h-full w-1 bg-gray-800"></div>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 triangle-down"></div>
                      </div>
                    )}
                    
                    {/* Main grid lines */}
                    {[0, 10, 20, 30, 40].map(bmi => (
                      <div 
                        key={bmi} 
                        className="absolute top-0 bottom-0 w-px bg-gray-300" 
                        style={{left: `${bmi / 40 * 100}%`}}
                      />
                    ))}
                    
                    {/* BMI threshold ticks */}
                    {[18.5, 25, 30, 35, 40].map(bmi => (
                      <div 
                        key={bmi} 
                        className="absolute top-0 h-2 w-0.5 bg-gray-600" 
                        style={{left: `${bmi / 40 * 100}%`}}
                      />
                    ))}
                  </div>
                  
                  <div className="relative">                   
                    {/* BMI threshold labels */}
                    <div className="absolute top-0 w-full">
                      {[
                        { bmi: 18.5, label: "18.5" },
                        { bmi: 25, label: "25" },
                        { bmi: 30, label: "30" },
                        { bmi: 35, label: "35" },
                        { bmi: 40, label: "40" }
                      ].map(({bmi, label}) => (
                        <div 
                          key={bmi}
                          className="absolute -top-0.5 text-[10px] text-gray-500 transform -translate-x-1/2"
                          style={{left: `${bmi / 40 * 100}%`}}
                        >
                          {label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-2 mt-3 text-xs bmi-legend">
                  {BMI_RANGES.map((range, index) => (
                    <div key={index} className="flex items-center bmi-legend-item">
                      <div className={`w-3 h-3 ${range.color} mr-1 rounded-sm border border-gray-200`}></div>
                      <span>{range.label}</span>
                    </div>
                  ))}
                </div>
                <div className="text-sm text-gray-600 mt-3">
                  <div className="flex justify-between">
                    <div>Height: <strong>{height} cm</strong></div>
                    <button 
                      className="text-xs text-blue-600 underline cursor-pointer" 
                      onClick={() => {
                        const weightGuideElement = document.getElementById('weight-guide');
                        if (weightGuideElement) {
                          weightGuideElement.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                    >
                      View weight guide for your height
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="col-span-2 bg-white p-3 rounded border">
                <div className="font-medium">Maintenance Calories (TDEE)</div>
                <div className="text-xl font-bold">{formatNumber(currentTDEE)} calories/day</div>
                <div className="text-sm text-gray-600 mt-1">
                  Based on your BMR of {formatNumber(currentBMR)} calories
                </div>
              </div>
              
              <div className="col-span-2 bg-white p-3 rounded border">
                <div className="font-medium">Recommended Macronutrients</div>
                <div className="grid grid-cols-3 mt-1 gap-2 text-center">
                  <div>
                    <div className="font-semibold">{macros.carbs}g</div>
                    <div className="text-xs text-gray-600">Carbs</div>
                  </div>
                  <div>
                    <div className="font-semibold">{macros.protein}g</div>
                    <div className="text-xs text-gray-600">Protein</div>
                  </div>
                  <div>
                    <div className="font-semibold">{macros.fat}g</div>
                    <div className="text-xs text-gray-600">Fat</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Projection Settings */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h2 className="font-semibold mb-3">Projection Settings</h2>
          
          <div className="flex flex-col gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Time Frame</label>
              <div className="flex items-center">
                <input
                  type="range"
                  min="4"
                  max="52"
                  value={timeFrame}
                  onChange={(e) => setTimeFrame(parseInt(e.target.value))}
                  className="w-full"
                />
                <span className="ml-3 w-16 text-center font-medium">{timeFrame} weeks</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Daily Calorie Deficit Levels</label>
              <div className="flex flex-wrap gap-2">
                {[250, 500, 750, 1000].map((deficit: number, index: number) => (
                  <label 
                    key={deficit} 
                    className={`flex items-center cursor-pointer p-2 border rounded transition-colors ${
                      deficitLevels.includes(deficit) 
                        ? 'bg-white border-gray-300 shadow-sm' 
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={deficitLevels.includes(deficit)}
                      onChange={() => {
                        if (deficitLevels.includes(deficit)) {
                          setDeficitLevels(deficitLevels.filter((d: number) => d !== deficit));
                        } else {
                          setDeficitLevels([...deficitLevels, deficit].sort((a: number, b: number) => a - b));
                        }
                      }}
                      className="mr-2"
                    />
                    <span style={{ color: deficitLevels.includes(deficit) ? getLineColor(index) : 'inherit' }}>
                      {deficit} cal
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Weight Projection Chart */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Weight Projection Over Time</h2>
          
          <div className="bg-white border rounded-lg p-4">
            <div className="h-64 md:h-80">
              {projectionData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={projectionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="week" 
                      label={{ value: 'Weeks', position: 'insideBottom', offset: -5 }} 
                    />
                    <YAxis 
                      label={{ 
                        value: `Weight (${weightUnit})`, 
                        angle: -90, 
                        position: 'insideLeft' 
                      }} 
                      domain={['auto', 'auto']}
                      tickFormatter={(value) => formatWeight(value)}
                    />
                    <Tooltip 
                      formatter={(value: any) => [formatWeight(Number(value)), 'Weight']}
                      labelFormatter={(value: any) => `Week ${value}`}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="current" 
                      name="No Deficit" 
                      stroke="#666" 
                      strokeDasharray="5 5"
                    />
                    {deficitLevels.map((deficit: number, index: number) => (
                      <Line
                        key={deficit}
                        type="monotone"
                        dataKey={`deficit${deficit}`}
                        name={currentTDEE * 0.4 < deficit ? 
                          `${deficit} cal/day (capped at ${Math.round(currentTDEE * 0.4)} cal)` : 
                          `${deficit} cal/day`}
                        stroke={getLineColor(index)}
                        strokeWidth={2}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  Please enter valid weight and height values to see projections
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Results Summary */}
        {projectionData.length > 0 && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-3">Projected Results</h3>
            <div className="grid grid-cols-1 gap-4">
              {deficitLevels.map((deficit: number, index: number) => {
                const finalWeekData = projectionData[projectionData.length - 1];
                const finalWeight = finalWeekData ? finalWeekData[`deficit${deficit}`] : getWeightInKg();
                const weightLoss = getWeightInKg() - finalWeight;
                const dailyCalories = currentTDEE - deficit;
                
                const finalBMI = finalWeight / ((height / 100) * (height / 100));
                const finalBMICategory = getBMICategory(finalBMI);
                
                // Calculate macros for this deficit level
                const deficitMacros = calculateMacros(dailyCalories);
                
                return (
                  <div 
                    key={deficit} 
                    className="border-l-4 p-3 bg-white rounded shadow-sm" 
                    style={{ borderColor: getLineColor(index) }}
                  >
                    <div className="font-bold text-lg">
                      {deficit} cal/day deficit
                      {currentTDEE * 0.4 < deficit && (
                        <span className="ml-2 text-sm font-normal text-orange-500">
                          (capped at {Math.round(currentTDEE * 0.4)} cal for safety)
                        </span>
                      )}
                    </div>
                    
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="col-span-full">
                        <span className="text-gray-600">Daily calorie target: </span>
                        <strong>
                          {formatNumber(currentTDEE - Math.min(deficit, currentTDEE * 0.4))} cal
                        </strong>
                      </div>
                      
                      <div>
                        <span className="text-gray-600">Final weight: </span>
                        <strong>{formatWeight(finalWeight)} {weightUnit}</strong>
                      </div>
                      
                      <div>
                        <span className="text-gray-600">Total loss: </span>
                        <strong className="text-green-600">-{formatWeight(weightLoss)} {weightUnit}</strong>
                      </div>
                      
                      <div>
                        <span className="text-gray-600">Weekly loss: </span>
                        <strong>{(weightLoss / timeFrame).toFixed(2)} {weightUnit}/week</strong>
                      </div>
                      
                      <div>
                        <span className="text-gray-600">Final BMI: </span>
                        <strong className={`${finalBMICategory.color} ${finalBMICategory.bgColor} px-2 py-0.5 rounded`}>
                          {finalBMI.toFixed(1)} - {finalBMICategory.text}
                        </strong>
                      </div>
                    </div>
                    
                    <div className="mt-2 pt-2 border-t text-sm">
                      <div className="font-medium mb-1">Recommended daily macros:</div>
                      <div className="flex justify-between text-center">
                        <div>
                          <div className="font-semibold">{deficitMacros.carbs}g</div>
                          <div className="text-xs text-gray-600">Carbs</div>
                        </div>
                        <div>
                          <div className="font-semibold">{deficitMacros.protein}g</div>
                          <div className="text-xs text-gray-600">Protein</div>
                        </div>
                        <div>
                          <div className="font-semibold">{deficitMacros.fat}g</div>
                          <div className="text-xs text-gray-600">Fat</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 text-sm text-gray-600 p-3 bg-white rounded">
              <h4 className="font-medium mb-1">Important Notes:</h4>
              <ul className="list-disc pl-4 space-y-1">
                <li>Calculations are based on estimates that 1 kg of body fat = 7700 calories (or 1 lb = 3500 calories).</li>
                <li>A safe rate of weight loss is generally 0.5-1 kg (1-2 lbs) per week. Faster weight loss may be difficult to maintain.</li>
                <li>The calculator prevents projections below healthy BMI levels (18.5) and deficit levels above 40% of your TDEE.</li>
                <li>Actual results may vary based on individual metabolism, muscle mass, water retention, and other factors.</li>
                <li>Always consult with healthcare professionals before starting any weight loss program, especially for deficits above 500 calories/day.</li>
              </ul>
            </div>
            
            {/* Training Suggestions Section */}
            <div className="mt-4">
              <h3 className="font-semibold mb-3">Home Workout Suggestions (Alternate Days)</h3>
              <div className="bg-white rounded p-4">
                <p className="mb-3">These workouts are designed for alternate days with minimal equipment (dumbbells up to 10kg and bodyweight exercises).</p>
                
                <div className="grid grid-cols-1 gap-4">
                  {/* Day A: Push Focus */}
                  <div className="border rounded p-3">
                    <h4 className="font-bold text-purple-700 mb-2">Day A: Push Focus</h4>
                    <ul className="space-y-2">
                      <li>
                        <div className="font-medium">1. Dumbbell Floor Press</div>
                        <div className="text-sm text-gray-600">3 sets x 8-12 reps | Lying on back, press dumbbells upward</div>
                      </li>
                      <li>
                        <div className="font-medium">2. Pike Push-ups</div>
                        <div className="text-sm text-gray-600">3 sets x 8-12 reps | Downward dog position, lower head toward floor</div>
                      </li>
                      <li>
                        <div className="font-medium">3. Dumbbell Lateral Raises</div>
                        <div className="text-sm text-gray-600">3 sets x 12-15 reps | Raise dumbbells to sides to shoulder height</div>
                      </li>
                      <li>
                        <div className="font-medium">4. Tricep Dips (using chair/sofa)</div>
                        <div className="text-sm text-gray-600">3 sets x 10-15 reps | Hands on edge, lower body using triceps</div>
                      </li>
                      <li>
                        <div className="font-medium">5. Diamond Push-ups</div>
                        <div className="text-sm text-gray-600">3 sets x 8-12 reps | Hands close together, focus on triceps</div>
                      </li>
                      <li>
                        <div className="font-medium">6. Plank</div>
                        <div className="text-sm text-gray-600">3 sets x 30-60 seconds | Hold position with straight back</div>
                      </li>
                    </ul>
                  </div>
                  
                  {/* Day B: Pull & Legs Focus */}
                  <div className="border rounded p-3">
                    <h4 className="font-bold text-blue-700 mb-2">Day B: Pull & Legs Focus</h4>
                    <ul className="space-y-2">
                      <li>
                        <div className="font-medium">1. Dumbbell Rows</div>
                        <div className="text-sm text-gray-600">3 sets x 10-12 reps per arm | Bent over, pull dumbbell to hip</div>
                      </li>
                      <li>
                        <div className="font-medium">2. Superman Holds</div>
                        <div className="text-sm text-gray-600">3 sets x 20-30 seconds | Lying face down, extend arms & legs</div>
                      </li>
                      <li>
                        <div className="font-medium">3. Goblet Squats</div>
                        <div className="text-sm text-gray-600">3 sets x 12-15 reps | Hold dumbbell at chest, perform squat</div>
                      </li>
                      <li>
                        <div className="font-medium">4. Dumbbell Lunges</div>
                        <div className="text-sm text-gray-600">3 sets x 10 reps per leg | Step forward into lunge position</div>
                      </li>
                      <li>
                        <div className="font-medium">5. Lying Leg Raises</div>
                        <div className="text-sm text-gray-600">3 sets x 12-15 reps | On back, raise straight legs upward</div>
                      </li>
                      <li>
                        <div className="font-medium">6. Dumbbell Romanian Deadlift</div>
                        <div className="text-sm text-gray-600">3 sets x 10-12 reps | Hinge at hips with soft knees</div>
                      </li>
                    </ul>
                  </div>
                  
                  {/* Day C: Full Body & Core */}
                  <div className="border rounded p-3">
                    <h4 className="font-bold text-green-700 mb-2">Day C: Full Body & Core</h4>
                    <ul className="space-y-2">
                      <li>
                        <div className="font-medium">1. Push-up to Dumbbell Row</div>
                        <div className="text-sm text-gray-600">3 sets x 8-10 reps | Push-up then row each dumbbell</div>
                      </li>
                      <li>
                        <div className="font-medium">2. Dumbbell Thrusters</div>
                        <div className="text-sm text-gray-600">3 sets x 10-12 reps | Squat then press dumbbells overhead</div>
                      </li>
                      <li>
                        <div className="font-medium">3. Russian Twists</div>
                        <div className="text-sm text-gray-600">3 sets x 20 reps (10 each side) | Rotate holding dumbbell</div>
                      </li>
                      <li>
                        <div className="font-medium">4. Mountain Climbers</div>
                        <div className="text-sm text-gray-600">3 sets x 30 seconds | Alternate bringing knees to chest</div>
                      </li>
                      <li>
                        <div className="font-medium">5. Glute Bridges</div>
                        <div className="text-sm text-gray-600">3 sets x 15 reps | Optional: dumbbell on hips for resistance</div>
                      </li>
                      <li>
                        <div className="font-medium">6. Lateral Plank Walks</div>
                        <div className="text-sm text-gray-600">3 sets x 10 steps each direction | Move sideways in plank</div>
                      </li>
                    </ul>
                  </div>
                  
                  {/* Cardio Options */}
                  <div className="border rounded p-3">
                    <h4 className="font-bold text-red-700 mb-2">Cardio Options (2-3 times weekly)</h4>
                    <ul className="space-y-2">
                      <li>
                        <div className="font-medium">1. HIIT Workout</div>
                        <div className="text-sm text-gray-600">20 minutes | 30s intense work, 30s rest</div>
                        <div className="text-xs mt-1">Example circuit: Jumping jacks, High knees, Burpees, Mountain climbers</div>
                      </li>
                      <li>
                        <div className="font-medium">2. Tabata Training</div>
                        <div className="text-sm text-gray-600">16 minutes | 20s work, 10s rest, 8 rounds per exercise</div>
                        <div className="text-xs mt-1">Pick 4 exercises, one round each (squats, push-ups, lunges, plank jacks)</div>
                      </li>
                      <li>
                        <div className="font-medium">3. Stair Climbing</div>
                        <div className="text-sm text-gray-600">15-20 minutes | Up and down stairs or use a sturdy step</div>
                      </li>
                      <li>
                        <div className="font-medium">4. Jump Rope</div>
                        <div className="text-sm text-gray-600">10-15 minutes | Mix of regular jumps and variations</div>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-4 bg-gray-50 p-3 rounded">
                  <h4 className="font-medium mb-2">Training Tips:</h4>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>Rest 1-2 minutes between sets and 2-3 minutes between exercises.</li>
                    <li>Aim for 3-4 workouts per week with at least one day of rest between similar workouts.</li>
                    <li>Start with lighter weights and focus on form before increasing intensity.</li>
                    <li>For progression, add 1-2 reps each week until you reach the upper range, then increase weight.</li>
                    <li>If an exercise is too challenging, try an easier variation first and build up strength.</li>
                    <li>Consider a weekly workout schedule like: Day A, Rest, Day B, Rest, Day C, Cardio, Rest.</li>
                    <li>Warm up for 5-10 minutes before each session with light cardio and dynamic stretches.</li>
                    <li>Cool down with 5-10 minutes of static stretching after each workout.</li>
                  </ul>
                </div>
                
                {/* Weight Guide by BMI Classification */}
                <div id="weight-guide" className="mt-4 bg-white p-4 rounded border">
                  <h3 className="font-semibold mb-2">Weight Guide for {height} cm Height</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    This table shows weight ranges for different BMI classifications at your current height.
                  </p>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border px-4 py-2 text-left">BMI Classification</th>
                          <th className="border px-4 py-2 text-right">BMI Range</th>
                          <th className="border px-4 py-2 text-right">Weight in {weightUnit}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {BMI_RANGES.filter(range => range.min > 0).map((range, index) => {
                          const minWeight = getWeightForBMI(range.min);
                          const maxWeight = range.max < 100 ? getWeightForBMI(range.max) : null;
                          
                          return (
                            <tr key={index} className={`${range.color}`}>
                              <td className="border px-4 py-2">
                                <div className="flex items-center">
                                  <div className={`w-3 h-3 ${range.color} border border-gray-300 mr-2 rounded-sm`}></div>
                                  {range.label}
                                </div>
                              </td>
                              <td className="border px-4 py-2 text-right">
                                {range.min} - {range.max < 100 ? range.max : '∞'}
                              </td>
                              <td className="border px-4 py-2 text-right">
                                {formatWeight(minWeight)} - {maxWeight ? formatWeight(maxWeight) : '∞'} {weightUnit}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-3 text-sm text-gray-600">
                    <p>
                      <strong>Note:</strong> A healthy BMI is generally considered to be between 18.5 and 24.9. 
                      However, BMI is just one indicator of health and doesn't account for factors like muscle mass, 
                      body composition, and individual health conditions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalorieDeficitCalculator;